import React, { useEffect, useState, useRef } from "react";
import { Container } from "./style";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Form,
  Input,
  Modal,
  Tabs,
  Avatar,
  message,
  Alert,
  Select,
} from "antd";
import {
  LeftOutlined,
  TagOutlined,
  EditOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import ReactRouterPrompt from "react-router-prompt";
import MockPhone from "../../../components/MockPhone/index";
import ScheduleMenu from "../../../components/ScheduleMenu/index";
import UserAvatar from "../../../assets/User.png";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { setPromptData } from "../../../store/features/CreatePrompt";
import useSubscriptionCheck from "../../../hooks/useSubscriptionCheck";
import SubscriptionModal from "../../../components/SubscriptionModal";

const { TextArea } = Input;

const CREATE_PROMPT = gql`
  mutation createPrompt($createPromptInput: CreatePromptInput!) {
    createPrompt(payload: $createPromptInput) {
      _id
    }
  }
`;
const UPDATE_PROMPT = gql`
  mutation updatePrompt($updatePromptInput: UpdatePromptInput!) {
    updatePrompt(payload: $updatePromptInput) {
      _id
    }
  }
`;

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
    }
  }
`;

export default function CreatePrompt() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [createPrompt] = useMutation(CREATE_PROMPT);
  const [updatePrompt] = useMutation(UPDATE_PROMPT);
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [groups, setGroups] = useState([]);
  const [params] = useSearchParams();
  const location = useLocation();
  const rowData = useSelector((state) => state.prompt.promptData);
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const promptId = rowData?._id;

  // Subscription check
  const {
    showModal,
    checkAndExecute,
    handleSubscriptionSuccess,
    handleModalCancel,
    upgradePromptKind,
  } = useSubscriptionCheck();

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo.coach?._id, isArchived: false },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  // Form
  const [form] = Form.useForm();

  useEffect(() => {
    if (location.pathname === "/CreatePrompt" && rowDataRef.current._id) {
      dispatch(setPromptData({}));
    }
    if (location.pathname === "/EditPrompt" && !rowDataRef.current._id) {
      navigate("/CreatePrompt");
    }
  }, [location, dispatch, navigate]);

  // Update prompt
  useEffect(() => {
    if (form && rowData._id) {
      // 根据 rowData 的 visibility 数据构建表单初始值
      let visibilityValue = "ALL_SUBSCRIBERS"; // 默认值
      if (rowData.visibility === "PUBLIC") {
        visibilityValue = "ALL";
      } else if (rowData.visibilityType === "ALL_SUBSCRIBERS") {
        visibilityValue = "ALL_SUBSCRIBERS";
      } else if (
        rowData.visibilityType === "GROUP" &&
        rowData.visibilityGroupId
      ) {
        visibilityValue = `GROUP_${rowData.visibilityGroupId}`;
      } else if (rowData.visibility === "PRIVATE") {
        visibilityValue = "ALL_SUBSCRIBERS";
      }
      form.setFieldsValue({
        ...rowData,
        visibility: visibilityValue,
      });
    }
  }, [rowData, form]);

  // Get content from AI tool and fill to the form
  const promptContent =
    params && params.getAll("content") && params.getAll("content")[0];
  useEffect(() => {
    if (promptContent) {
      form.setFieldsValue({ content: promptContent });
    }
  }, [promptContent, form]);

  const contentValue = Form.useWatch("content", form);
  const visibleValue = Form.useWatch("visibility", form);

  useEffect(() => {
    if (
      (contentValue && contentValue !== rowDataRef.current.content) ||
      (visibleValue && visibleValue !== rowDataRef.current.visibleValue)
    ) {
      setIsChanged(true);
    }
  }, [contentValue, visibleValue]);
  useEffect(() => {
    if (loading) {
      setIsChanged(false);
    }
  }, [loading]);

  const onSubmit = async ({ values, status, scheduledAt }) => {
    setLoading(true);
    try {
      if (!promptId) {
        const param = {
          status,
          authorName: userInfo.user?.name,
          coach: userInfo.coach?._id,
          createdBy: userInfo.coach?._id,
          date: new Date(),
          content: values.content,
          visibility: "PRIVATE",
          visibilityType: "ALL_SUBSCRIBERS",
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        if (scheduledAt) {
          param.scheduledAt = scheduledAt;
        }
        await createPrompt({
          variables: {
            createPromptInput: param,
          },
        });
        message.success("Reflection has been created!");
      } else {
        const param = {
          _id: promptId,
          status,
          createdBy: userInfo.coach?._id,
          content: values.content,
          visibility: "PRIVATE",
          visibilityType: "ALL_SUBSCRIBERS",
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        if (scheduledAt) {
          param.scheduledAt = scheduledAt;
        }
        await updatePrompt({
          variables: {
            updatePromptInput: param,
          },
        });
        message.success("Reflection has been updated!");
      }
      dispatch(setPromptData({}));
      navigate("/contentManager?tabName=3");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    console.log("Success:", values);
    // Check subscription before publishing
    checkAndExecute(() => {
      onSubmit({ values, status: "PUBLISHED" });
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setLoading(false);
  };

  const onDraft = () => {
    const values = form.getFieldsValue();
    onSubmit({ values, status: "DRAFTED" });
  };

  const onBack = () => {
    navigate("/contentManager?tabName=3");
  };

  const onBackAI = () => {
    navigate("/AiPrompt");
  };

  return (
    <Container forceRender>
      <ReactRouterPrompt when={isChanged}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              title="Unsaved changes"
              open
              footer={[
                <div key="flex-between" className="flex-between">
                  <Button
                    key="discard"
                    danger
                    loading={loading}
                    onClick={() => {
                      setIsChanged(false);
                      onConfirm();
                    }}
                  >
                    Discard Changes
                  </Button>
                  <div key="div">
                    <Button key="back" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      key="save"
                      type="primary"
                      loading={loading}
                      onClick={onDraft}
                    >
                      Save Draft
                    </Button>
                  </div>
                </div>,
              ]}
            >
              <p>
                You are about to leave this page with unsaved changes. Would you
                like to save these changes before leaving?
              </p>
            </Modal>
          )
        }
      </ReactRouterPrompt>
      <div className="page-form">
        <div className="left-side">
          {!promptContent && (
            <Button
              className="btn-back"
              icon={<LeftOutlined />}
              type="text"
              onClick={onBack}
            >
              Content Manager
            </Button>
          )}
          {promptContent && (
            <Button
              className="btn-back"
              icon={<LeftOutlined />}
              type="text"
              onClick={onBackAI}
            >
              AI Copilot
            </Button>
          )}

          <div className="page-title">
            {!promptId ? "Create a new Reflection" : "Update a Reflection"}
          </div>
          <Form
            form={form}
            name="promptForm"
            layout="vertical"
            size="large"
            className="full-width"
            initialValues={{
              visibility: "ALL_SUBSCRIBERS",
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label="Enter your reflection"
              name="content"
              rules={[{ required: true, message: "Please input reflection" }]}
            >
              <TextArea
                showCount
                maxLength={300}
                placeholder="Empty"
                style={{ resize: "none", height: 200 }}
              />
            </Form.Item>
            <Form.Item
              label="Visible to"
              name="visibility"
              rules={[{ required: true, message: "Please select visibility" }]}
            >
              <Select placeholder="Select visibility" size="large">
                <Select.Option value="ALL_SUBSCRIBERS">
                  All Subscribers
                </Select.Option>
                {groups.map((group) => (
                  <Select.Option key={group._id} value={`GROUP_${group._id}`}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <br />
            {rowData.status === "SCHEDULED" && rowData.scheduledAt && (
              <div>
                <Alert
                  className="full-width clock-tag"
                  message={`This reflection is scheduled to be published at ${new Date(
                    rowData.scheduledAt
                  ).toLocaleString()}.`}
                  icon={<ClockCircleOutlined />}
                  type="success"
                  showIcon
                />
              </div>
            )}
            <Form.Item>
              <ScheduleMenu
                id={promptId}
                showSchedule={!promptId || rowData.status !== "PUBLISHED"}
                form={form}
                loading={loading}
                onSubmit={onSubmit}
              ></ScheduleMenu>
              <Button
                block
                className="mt-16"
                loading={loading}
                onClick={onDraft}
              >
                Save as draft
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <MockPhone showTabbar>
            <div className="prompt-top gray-all">
              <h3>Hello {userInfo?.coach?.name}</h3>
              <Tabs
                defaultActiveKey="1"
                items={[
                  { key: "1", label: "For you" },
                  { key: "2", label: "Featured" },
                ]}
              ></Tabs>
            </div>
            <div className="flex-between mb-10">
              <div>Reflections</div>
              <Button type="text">
                <b>More</b>
              </Button>
            </div>
            <div className="info-box">
              <div className="flex-between mb-10">
                <div className="c-gray">By {userInfo?.coach?.name}</div>
                <TagOutlined />
              </div>
              <div className="info-content">{contentValue || "Empty"}</div>
              <div className="text-right">
                <Button type="primary" icon={<EditOutlined />}>
                  Write
                </Button>
              </div>
            </div>
            <div className="flex-between mb-10 gray-all">
              <div>Today's Insights</div>
              <Button type="text">
                <b>More</b>
              </Button>
            </div>
            <div className="info-box gray-all">
              <div className="flex-between mb-10">
                <div>
                  <Avatar src={UserAvatar} className="mr-10" />
                  Character Lab
                  <span className="c-gray ml-10">FEB 24, 2023</span>
                </div>
                <TagOutlined />
              </div>
              <div className="info-content">
                Why does gratitude matter? When you feel gratitude, you feel a
                sense of abundance. When you express gratitude—especially when
                it’s heartfelt—you strengthen your relationships with others.
                Grateful people are happier and more fulfilled. And gratitude
                leads you to be nicer to other people: more cooperative,
                patient, and trusting.
              </div>
            </div>
            <div className="flex-between mb-10 gray-all">
              <div>Practices Recommended for you</div>
              <Button type="text">
                <b>More</b>
              </Button>
            </div>
          </MockPhone>
        </div>
      </div>
      <SubscriptionModal
        open={showModal}
        onSuccess={handleSubscriptionSuccess}
        onCancel={handleModalCancel}
        checkoutPromptKind={upgradePromptKind ?? "trial"}
      />
    </Container>
  );
}
